import {useMessage} from '@coolcolduk/ui-mui';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router';
import useGetGenerationJobProject from '../../../hook/generationJob/useGetGenerationJobProject';
import useGdriveTokens from '../../../hook/useGdriveTokens';
import useUpdateGenerationJobProject from '../../../hook/generationJob/useUpdateGenerationJobProject';
import type {GenerationJobProjectInput, OutputQuality, OutputShape} from '../../../hook/generationJob/schema';

function sheetIdToUrl(sheetId: string): string {
  return `https://docs.google.com/spreadsheets/d/${sheetId}`;
}

function outputFolderIdToUrl(outputFolderId: string): string {
  return `https://drive.google.com/drive/folders/${outputFolderId}`;
}

const EDIT_PATH_PREFIX = '/image-to-image/edit/';

function projectIdFromLocation(pathname: string): string | undefined {
  if (!pathname.startsWith(EDIT_PATH_PREFIX)) return undefined;
  const segment = pathname.slice(EDIT_PATH_PREFIX.length).split('/')[0];
  return segment || undefined;
}

export default function ImageToImageEditProjectPage() {
  const {showMessage} = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{id: string}>();
  const projectId = params.id ?? projectIdFromLocation(location.pathname);

  const {tokens: gdriveTokens, loading: gdriveTokensLoading} = useGdriveTokens();
  const [projectName, setProjectName] = useState('');
  const [gdriveTokenId, setGdriveTokenId] = useState('');
  const [sourceImageFileId, setSourceImageFileId] = useState('');
  const [sourceImageUrl, setSourceImageUrl] = useState('');
  const [sheetUrlOrId, setSheetUrlOrId] = useState('');
  const [outputFolderUrlOrId, setOutputFolderUrlOrId] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [edit, setEdit] = useState(false);
  const [outputQuality, setOutputQuality] = useState<OutputQuality>('auto');
  const [outputShape, setOutputShape] = useState<OutputShape>('auto');

  const {project, loading: projectLoading} = useGetGenerationJobProject(projectId);

  const OUTPUT_QUALITY_OPTIONS: OutputQuality[] = ['auto', 'low', 'medium', 'high'];
  const OUTPUT_SHAPE_OPTIONS: OutputShape[] = ['auto', 'square', 'portrait', 'landscape'];
  const [updateProject, {loading: updateLoading}] = useUpdateGenerationJobProject({
    onCompleted: () => {
      showMessage('Project updated');
      navigate('/image-to-image');
    },
    onError: (e) => showMessage(`Error: ${e.message}`, undefined, 'error'),
  });

  useEffect(() => {
    if (project) {
      setProjectName(project.name ?? '');
      setGdriveTokenId(project.gdriveTokenId ?? '');
      setSourceImageFileId(project.sourceImageFileId ?? '');
      setSourceImageUrl(project.sourceImageUrl ?? '');
      setSheetUrlOrId(project.sheetId ? sheetIdToUrl(project.sheetId) : '');
      setOutputFolderUrlOrId(project.outputFolderId ? outputFolderIdToUrl(project.outputFolderId) : '');
      setSystemPrompt(project.systemPrompt ?? '');
      setEdit(project.edit ?? false);
      setOutputQuality((project.outputQuality as OutputQuality) ?? 'auto');
      setOutputShape((project.outputShape as OutputShape) ?? 'auto');
    }
  }, [project]);

  const handleSubmit = () => {
    if (!projectId) return;
    if (!gdriveTokenId.trim()) {
      showMessage('Select a Google Drive account', undefined, 'warning');
      return;
    }
    if (!sheetUrlOrId.trim() || !outputFolderUrlOrId.trim()) {
      showMessage('Fill sheet and output folder', undefined, 'warning');
      return;
    }
    updateProject(projectId, {
      name: projectName || undefined,
      gdriveTokenId: gdriveTokenId.trim(),
      sourceImageFileId: sourceImageFileId.trim() || undefined,
      sourceImageUrl: sourceImageUrl.trim() || undefined,
      sheetUrlOrId: sheetUrlOrId.trim(),
      outputFolderUrlOrId: outputFolderUrlOrId.trim(),
      systemPrompt: systemPrompt.trim() || undefined,
      edit,
      outputQuality,
      outputShape,
    } as GenerationJobProjectInput);
  };

  if (projectLoading || gdriveTokensLoading || !projectId) {
    return <Typography color="text.secondary">Loading…</Typography>;
  }
  if (!project) {
    return (
      <Stack spacing={2}>
        <Typography color="text.secondary">Project not found.</Typography>
        <Button component={Link} to="/image-to-image" variant="outlined">
          Back to projects
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Edit project</Typography>
      <Typography variant="body2" color="text.secondary">
        Update the project. Use a linked Google Drive account and provide source image, sheet, and output folder.
      </Typography>

      <Button component={Link} to="/image-to-image" variant="outlined" sx={{alignSelf: 'flex-start'}}>
        Back to projects
      </Button>

      <FormControl fullWidth required>
        <InputLabel id="gdrive-account-label">Google Drive account</InputLabel>
        <Select
          labelId="gdrive-account-label"
          value={gdriveTokenId}
          label="Google Drive account"
          onChange={(e) => setGdriveTokenId(e.target.value)}
        >
          {gdriveTokens.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name} {t.email ? `(${t.email})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Project name (optional)"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
      />

      <TextField
        fullWidth
        label="Source image (optional) – paste Drive link or file id"
        value={sourceImageUrl || sourceImageFileId}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (v.startsWith('http')) {
            setSourceImageUrl(v);
            setSourceImageFileId('');
          } else {
            setSourceImageFileId(v);
            setSourceImageUrl('');
          }
        }}
        placeholder="https://drive.google.com/... or file id"
      />

      <FormControlLabel
        control={<Checkbox checked={edit} onChange={(e) => setEdit(e.target.checked)} />}
        label="Use edit endpoint (required for reference/source images; when unchecked, generations endpoint is used — text-to-image only, no input images)"
      />

      <FormControl fullWidth>
        <InputLabel id="output-quality-label">Output quality</InputLabel>
        <Select
          labelId="output-quality-label"
          value={outputQuality}
          label="Output quality"
          onChange={(e) => setOutputQuality(e.target.value as OutputQuality)}
        >
          {OUTPUT_QUALITY_OPTIONS.map((q) => (
            <MenuItem key={q} value={q}>
              {q}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel id="output-shape-label">Output shape</InputLabel>
        <Select
          labelId="output-shape-label"
          value={outputShape}
          label="Output shape"
          onChange={(e) => setOutputShape(e.target.value as OutputShape)}
        >
          {OUTPUT_SHAPE_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={2}
        label="System prompt (optional)"
        value={systemPrompt}
        onChange={(e) => setSystemPrompt(e.target.value)}
        placeholder="Optional instructions applied to every image in this project…"
      />

      <TextField
        fullWidth
        label="Google Sheet URL (columns: filename, prompt, status)"
        value={sheetUrlOrId}
        onChange={(e) => setSheetUrlOrId(e.target.value)}
        placeholder="https://docs.google.com/spreadsheets/d/..."
      />

      <TextField
        fullWidth
        label="Output folder (Google Drive link or folder id)"
        value={outputFolderUrlOrId}
        onChange={(e) => setOutputFolderUrlOrId(e.target.value)}
        placeholder="https://drive.google.com/..."
      />

      <Button variant="contained" color="primary" onClick={handleSubmit} disabled={updateLoading}>
        Save changes
      </Button>
    </Stack>
  );
}
